/**
 * BountiesShelf — holder-facing bounties browse + submit inside a Clan Feed.
 *
 * Renders every active bounty for a Clan. Each card shows the reward,
 * proof type, deadline, and either a "Submit proof" CTA or the fan's
 * current submission status (pending / won / rejected).
 *
 * Endpoints:
 *   GET  /api/clans/{handle}/bounties            — bounties + viewer ctx
 *   POST /api/clans/{handle}/bounties/{id}/submit — submit proof
 *   POST /api/files/upload                       — screenshot upload
 */
import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import api, { formatApiErrorDetail, uploadImage } from '../api';

const fmtPts = (n) => `${Number(n || 0).toFixed(2)} pts`;

const fmtRelDate = (iso) => {
  if (!iso) return '';
  const d = new Date(iso);
  const s = Math.floor((d.getTime() - Date.now()) / 1000);
  if (s < 0) return 'ended';
  const m = Math.floor(s / 60);
  if (m < 60) return `${m}m left`;
  const h = Math.floor(m / 60);
  if (h < 24) return `${h}h left`;
  const dd = Math.floor(h / 24);
  return `${dd}d left`;
};

export default function BountiesShelf({ clanHandle, clanName, onToast }) {
  const navigate = useNavigate();
  const [state, setState] = useState(null);
  const [submitting, setSubmitting] = useState(null);   // bounty being submitted

  const load = useCallback(async () => {
    try {
      const { data } = await api.get(`/clans/${clanHandle}/bounties`);
      setState(data);
    } catch (ex) {
      onToast?.({ msg: formatApiErrorDetail(ex.response?.data?.detail) || 'Could not load bounties.', kind: 'error' });
      setState({ bounties: [], viewer: {} });
    }
  }, [clanHandle, onToast]);

  useEffect(() => { load(); }, [load]);

  if (!state) return <div className="cf-empty">Loading bounties…</div>;

  const { bounties, viewer } = state;
  const activeBounties = bounties.filter((b) => b.status === 'active');

  if (bounties.length === 0) {
    return (
      <div className="cf-empty" data-testid="cf-bounties-empty">
        <div style={{ fontSize: 48, opacity: 0.4, marginBottom: 12 }}>🎯</div>
        <div style={{ fontWeight: 700, fontSize: 16, marginBottom: 6 }}>No bounties yet</div>
        <div style={{ fontSize: 13, opacity: 0.7 }}>
          {clanName || 'The creator'} hasn&apos;t posted any bounties. Check back soon.
        </div>
      </div>
    );
  }

  return (
    <div data-testid="cf-panel-bounties">
      {/* Header strip — viewer status */}
      {viewer.is_authenticated && !viewer.is_creator && (
        <div
          style={{
            padding: '10px 14px', borderRadius: 10, marginBottom: 14,
            background: 'rgba(0,180,255,0.06)',
            border: '1px solid rgba(0,180,255,0.2)',
            fontSize: 13, display: 'flex', justifyContent: 'space-between', flexWrap: 'wrap', gap: 8,
          }}
        >
          <span>You hold <strong>{viewer.bits_owned} Bit{viewer.bits_owned === 1 ? '' : 's'}</strong> in this Clan</span>
          {viewer.bits_owned < (viewer.min_bits_to_join || 1) && (
            <span style={{ opacity: 0.8 }}>
              Need <strong>{viewer.min_bits_to_join}</strong> to submit
            </span>
          )}
        </div>
      )}

      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(280px, 1fr))', gap: 12 }}>
        {activeBounties.map((b) => (
          <BountyCard
            key={b.id}
            bounty={b}
            viewer={viewer}
            onSubmit={() => setSubmitting(b)}
            onNavigate={navigate}
          />
        ))}
      </div>

      {submitting && (
        <SubmitProofModal
          bounty={submitting}
          clanHandle={clanHandle}
          onClose={() => setSubmitting(null)}
          onDone={(msg) => { setSubmitting(null); onToast?.({ msg }); load(); }}
          onToast={onToast}
        />
      )}
    </div>
  );
}


function BountyCard({ bounty, viewer, onSubmit, onNavigate }) {
  const mine = bounty.my_submission;
  const filledPct = bounty.winner_count ? Math.round((bounty.winners_selected / bounty.winner_count) * 100) : 0;
  const slotsLeft = Math.max(0, bounty.winner_count - bounty.winners_selected);
  const canSubmit = viewer.is_authenticated
    && !viewer.is_creator
    && viewer.bits_owned >= (viewer.min_bits_to_join || 1)
    && !mine
    && slotsLeft > 0;

  return (
    <div
      style={{
        padding: 16, borderRadius: 14,
        background: 'linear-gradient(135deg, rgba(0,180,255,0.06), rgba(181,132,245,0.06))',
        border: '1px solid rgba(0,180,255,0.2)',
        display: 'flex', flexDirection: 'column', height: '100%',
      }}
      data-testid={`cf-bounty-card-${bounty.id}`}
    >
      <div style={{ display: 'flex', gap: 6, marginBottom: 8, flexWrap: 'wrap' }}>
        <span style={pillStyle('#00B4FF', 'rgba(0,180,255,0.15)')}>🎯 Bounty</span>
        {bounty.ends_at && (
          <span style={pillStyle('#FFB74D', 'rgba(255,183,77,0.15)')}>{fmtRelDate(bounty.ends_at)}</span>
        )}
      </div>

      <div style={{ fontWeight: 700, fontSize: 15, marginBottom: 4 }}>{bounty.title}</div>
      {bounty.description && (
        <div style={{ fontSize: 13, opacity: 0.8, lineHeight: 1.4, marginBottom: 10 }}>
          {bounty.description}
        </div>
      )}

      <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', marginBottom: 10 }}>
        <span style={pillStyle('#B4FF00', 'rgba(180,255,0,0.15)')}>
          {fmtPts(bounty.reward_per_winner_pts)} / winner
        </span>
        <span style={pillStyle('inherit', 'rgba(255,255,255,0.08)')}>
          {slotsLeft} / {bounty.winner_count} slots left
        </span>
      </div>

      <div style={{ height: 4, borderRadius: 4, background: 'rgba(255,255,255,0.08)', overflow: 'hidden', marginBottom: 12 }}>
        <div style={{ width: `${filledPct}%`, height: '100%', background: 'linear-gradient(90deg, #00B4FF, #B584F5)' }} />
      </div>

      <div style={{ marginTop: 'auto' }}>
      {mine ? (
        <MySubmissionCTA sub={mine} />
      ) : viewer.is_creator ? (
        <button
          onClick={() => onNavigate('/manage-clan')}
          className="mc-btn-secondary"
          style={{ width: '100%', fontSize: 13, padding: '8px 12px' }}
          data-testid={`cf-bounty-manage-${bounty.id}`}
        >
          ✏️ Review in Manage
        </button>
      ) : canSubmit ? (
        <button
          onClick={onSubmit}
          className="mc-btn-primary"
          style={{ width: '100%', fontSize: 13, padding: '10px 14px' }}
          data-testid={`cf-bounty-submit-${bounty.id}`}
        >
          Submit proof
        </button>
      ) : !viewer.is_authenticated ? (
        <button
          onClick={() => onNavigate('/login')}
          className="mc-btn-secondary"
          style={{ width: '100%', fontSize: 13, padding: '8px 12px' }}
        >
          Sign in to submit
        </button>
      ) : slotsLeft === 0 ? (
        <div style={{ textAlign: 'center', padding: '8px 0', fontSize: 12, opacity: 0.6 }}>
          All slots filled
        </div>
      ) : (
        <div style={{ textAlign: 'center', padding: '8px 0', fontSize: 12, opacity: 0.7 }}>
          Buy {viewer.min_bits_to_join - viewer.bits_owned} more Bit{(viewer.min_bits_to_join - viewer.bits_owned) === 1 ? '' : 's'} to unlock
        </div>
      )}
      </div>
    </div>
  );
}


function MySubmissionCTA({ sub }) {
  const map = {
    pending:  { text: '⏳ Submission pending review', bg: 'rgba(255,183,77,0.15)', color: '#FFB74D' },
    won:      { text: `🏆 You won ${Number(sub.reward_paid_pts || 0).toFixed(2)} pts!`, bg: 'rgba(180,255,0,0.18)', color: '#B4FF00' },
    rejected: { text: '✗ Submission rejected', bg: 'rgba(255,87,34,0.15)', color: '#FF7043' },
  };
  const s = map[sub.status] || map.pending;
  return (
    <div
      style={{
        padding: '10px 14px', borderRadius: 10, textAlign: 'center',
        background: s.bg, color: s.color, fontWeight: 700, fontSize: 13,
      }}
      data-testid={`cf-bounty-status-${sub.id}`}
    >
      {s.text}
      {sub.reviewer_note && (
        <div style={{ fontSize: 11, opacity: 0.75, marginTop: 4, fontWeight: 400, fontStyle: 'italic' }}>
          "{sub.reviewer_note}"
        </div>
      )}
    </div>
  );
}


function SubmitProofModal({ bounty, clanHandle, onClose, onDone, onToast }) {
  const [content, setContent] = useState('');
  const [uploading, setUploading] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const fileRef = useRef(null);

  const isScreenshot = bounty.proof_type === 'screenshot';
  const isUrl = bounty.proof_type === 'url';

  const onFile = async (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    if (f.size > 5 * 1024 * 1024) {
      onToast?.({ msg: 'Image must be under 5MB', kind: 'error' });
      return;
    }
    setUploading(true);
    try {
      const { url } = await uploadImage(f);
      setContent(url);
    } catch (ex) {
      onToast?.({ msg: formatApiErrorDetail(ex.response?.data?.detail) || ex.message || 'Upload failed', kind: 'error' });
    } finally {
      setUploading(false);
    }
  };

  const submit = async () => {
    if (submitting) return;
    const trimmed = (content || '').trim();
    if (!trimmed) {
      onToast?.({ msg: 'Please provide your proof first', kind: 'error' });
      return;
    }
    if (isUrl && !/^https?:\/\//i.test(trimmed)) {
      onToast?.({ msg: 'URL must start with http:// or https://', kind: 'error' });
      return;
    }
    setSubmitting(true);
    try {
      const { data } = await api.post(`/clans/${clanHandle}/bounties/${bounty.id}/submit`, {
        proof_content: trimmed,
      });
      const won = data?.submission?.status === 'won';
      onDone(won ? `🏆 You won ${Number(data.submission.reward_paid_pts).toFixed(2)} pts!` : '✓ Submitted — creator will review');
    } catch (ex) {
      onToast?.({ msg: formatApiErrorDetail(ex.response?.data?.detail) || 'Could not submit', kind: 'error' });
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div
      onClick={onClose}
      style={{
        position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.7)',
        display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 300, padding: 20,
      }}
      data-testid="submit-bounty-modal"
    >
      <div
        onClick={(e) => e.stopPropagation()}
        style={{
          maxWidth: 480, width: '100%',
          background: 'rgba(22,26,36,0.98)', backdropFilter: 'blur(20px)',
          borderRadius: 16, padding: 24, border: '1px solid rgba(0,180,255,0.3)',
        }}
      >
        <div style={{ fontSize: 18, fontWeight: 700, marginBottom: 4 }}>🎯 {bounty.title}</div>
        <div style={{ fontSize: 12, opacity: 0.7, marginBottom: 16 }}>
          Win <strong>{fmtPts(bounty.reward_per_winner_pts)}</strong> · {bounty.selection_mode === 'first_come' ? 'First to submit wins instantly' : 'Creator will review'}
        </div>

        {bounty.proof_instructions && (
          <div style={{
            padding: '10px 12px', borderRadius: 8, marginBottom: 14,
            background: 'rgba(0,180,255,0.06)', border: '1px solid rgba(0,180,255,0.15)',
            fontSize: 12, lineHeight: 1.5,
          }}>
            📋 {bounty.proof_instructions}
          </div>
        )}

        {isScreenshot ? (
          <>
            <div style={{ fontSize: 11, opacity: 0.7, textTransform: 'uppercase', letterSpacing: '0.05em', fontWeight: 700, marginBottom: 6 }}>
              Upload proof image
            </div>
            <input
              ref={fileRef}
              type="file"
              accept="image/*"
              onChange={onFile}
              style={{ display: 'none' }}
              data-testid="submit-bounty-file"
            />
            {content ? (
              <div style={{ position: 'relative' }}>
                <img src={content} alt="proof" style={{ maxWidth: '100%', maxHeight: 260, borderRadius: 8, objectFit: 'cover' }} />
                <button
                  className="mc-btn-secondary"
                  style={{ marginTop: 8, fontSize: 12 }}
                  onClick={() => { setContent(''); fileRef.current && (fileRef.current.value = ''); }}
                  disabled={submitting}
                >
                  Replace image
                </button>
              </div>
            ) : (
              <button
                className="mc-btn-secondary"
                onClick={() => fileRef.current?.click()}
                disabled={uploading}
                style={{ width: '100%', padding: '18px 12px' }}
                data-testid="submit-bounty-pick-file"
              >
                {uploading ? 'Uploading…' : '📸 Pick a screenshot (max 5MB)'}
              </button>
            )}
          </>
        ) : isUrl ? (
          <>
            <div style={{ fontSize: 11, opacity: 0.7, textTransform: 'uppercase', letterSpacing: '0.05em', fontWeight: 700, marginBottom: 6 }}>
              Paste your URL
            </div>
            <input
              className="mc-input"
              type="url"
              value={content}
              onChange={(e) => setContent(e.target.value)}
              placeholder="https://…"
              maxLength={2000}
              data-testid="submit-bounty-url"
            />
          </>
        ) : (
          <>
            <div style={{ fontSize: 11, opacity: 0.7, textTransform: 'uppercase', letterSpacing: '0.05em', fontWeight: 700, marginBottom: 6 }}>
              Your answer
            </div>
            <textarea
              className="mc-textarea"
              value={content}
              onChange={(e) => setContent(e.target.value.slice(0, 2000))}
              placeholder="Type your reply…"
              rows={5}
              maxLength={2000}
              data-testid="submit-bounty-text"
            />
          </>
        )}

        <div style={{ display: 'flex', gap: 10, marginTop: 18, justifyContent: 'flex-end' }}>
          <button className="mc-btn-secondary" onClick={onClose} disabled={submitting} data-testid="submit-bounty-cancel">
            Cancel
          </button>
          <button className="mc-btn-primary" onClick={submit} disabled={submitting || uploading} data-testid="submit-bounty-confirm">
            {submitting ? 'Submitting…' : 'Submit proof'}
          </button>
        </div>
      </div>
    </div>
  );
}


function pillStyle(color, bg) {
  return {
    padding: '3px 8px', fontSize: 11, borderRadius: 4,
    background: bg, color, fontWeight: 700,
  };
}
